import React, { useState, useEffect } from "react";
import { apiList } from "../../../helpers/apiList";
import CustomAlert from "../../../helpers/CustomAlert";
import { axiosDeleteApi, axiosGetApi, axiosPostApi } from "../../../helpers/interceptorServices";
import AddVectorIcon from "../../../assets/Icons/Add Vector.png";
import CrossIcon from "../../../assets/Icons/cross_svg.svg";
import LeftCircle from "../../../assets/Icons/Left circle B.png";
import RightCircle from "../../../assets/Icons/Right circle A.png";
import {
  Truck,
  MapPin,
  Scale,
  Ruler,
  Package,
  IndianRupee,
  Percent,
} from "lucide-react";
import Dropdown from "../../../components/Dropdown";
import GlobalDeleteConfirmationModal from "../../../components/common/GlobalDeleteConfirmationModal";

const AddChargeMasterModal = ({
  show,
  handleClose,
  callApi,
  editData,
  action,
  columnData,
  setCurrentPage,
}) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const [formData, setFormData] = useState({
    source: "",
    weight: "",
    height: "",
    length: "",
    width: "",
    chargesAmount: "",
    fuelCharges: "",
    gst: "",
    totalCharges: "",
    express: "",
    rateCardType: "",
    additionalWeightUnit: "",
    additionalRate: "",
  });
  const [errors, setErrors] = useState({});
  const [cardOptions, setCardOptions] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isViewMode = action === "view";
  const isDeleteMode = action === "delete";
  const isEditMode = action === "modify";
  const isReadOnly = isViewMode || isDeleteMode;

  // Helper: format number to 2 decimal places as string
  const formatToTwoDecimals = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const num = parseFloat(value);
    if (isNaN(num)) return "";
    return num.toFixed(2);
  };

  useEffect(() => {
    if (action === "add") {
      setFormData({
        source: "",
        weight: "",
        height: "",
        length: "",
        width: "",
        chargesAmount: "",
        fuelCharges: "",
        gst: "",
        totalCharges: "",
        express: "",
        rateCardType: "",
        additionalWeightUnit: "",
        additionalRate:"",
      });
    } else if (isEditMode || isViewMode || isDeleteMode) {
      setFormData({
        ...editData,
        source: columnData?.source ?? "",

        weight: formatToTwoDecimals(columnData?.weightE ?? ""),
        height: formatToTwoDecimals(columnData?.heightE ?? ""),
        length: formatToTwoDecimals(columnData?.lengthE ?? ""),
        width: formatToTwoDecimals(columnData?.widthE ?? ""),
  
        chargesAmount: formatToTwoDecimals(columnData?.amountE ?? ""),
        fuelCharges: formatToTwoDecimals(columnData?.fuelChargesE ?? ""),
        gst: formatToTwoDecimals(columnData?.gst ?? ""),
        totalCharges: formatToTwoDecimals(columnData?.totalChargesE ?? ""),
  
        express: columnData?.express ?? "",
        rateCardType: columnData?.rateCardId ?? "",
  
        additionalWeightUnit: formatToTwoDecimals(
          columnData?.additionalWeightUnitE ?? ""
        ),
        additionalRate: formatToTwoDecimals(
          columnData?.additionalRateE ?? ""
        ),
      });
    }
    setErrors({});
  }, [show, action, editData, columnData]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (
      [
        "weight",
        "height",
        "length",
        "width",
        "chargesAmount",
        "fuelCharges",
        "gst",
        "additionalWeightUnit",
        "additionalRate",
      ].includes(id)
    ) {
      if (value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    }
    // setFormData((prev) => ({ ...prev, [id]: value }));
    const calculateTotal = (chargesAmount, fuel, gst) => {
      if (!chargesAmount || !fuel || !gst) return "";
      const amt = Number(chargesAmount) || 0;
      const fuelC = Number(fuel) || 0;
      const gstVal = Number(gst) || 0;
      const gstAmount = ((amt + fuelC) * gstVal) / 100;
      return (amt + fuelC + gstAmount).toFixed(2);
    };
    setFormData((prev) => {
      const updated = { ...prev, [id]: value };
      updated.totalCharges = calculateTotal(
        updated.chargesAmount,
        updated.fuelCharges,
        updated.gst
      );
      return updated;
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.express.trim()) {
      newErrors.express = "Please select an Express Type";
    }
    if (!formData.source.trim()) {
      newErrors.source = "Please select a Source";
    }
    if (!formData.rateCardType) {
      newErrors.rateCardType = "Please select a Rate Card Type";
    }

    const numFields = [
      { id: "weight", label: "Weight", max: 1000 },
      { id: "height", label: "Height", max: 500 },
      { id: "length", label: "Length", max: 500 },
      { id: "width", label: "Width", max: 500 },
      { id: "chargesAmount", label: "Charges Amount", max: 9999999 },
      { id: "fuelCharges", label: "Fuel Charges", max: 9999999 },
      { id: "gst", label: "GST", max: 100 },
      { id: "additionalWeightUnit", label: "Additional Weight Unit", max: 1000 },
      { id: "additionalRate", label: "Additional Rate", max: 9999999 },
    ];
    numFields.forEach(({ id, label, max }) => {
      const val = formData[id];
      const strVal = typeof val === "string" ? val.trim() : val;
      if (strVal === "" || strVal === undefined || strVal === null) {
        newErrors[id] = `${label} is required`;
      } else if (isNaN(strVal)) {
        newErrors[id] = `Enter a valid number for ${label.toLowerCase()}`;
      } else if (Number(strVal) < 0) {
        newErrors[id] = `${label} must be 0 or a positive number`;
      } else if (Number(strVal) > max) {
        newErrors[id] = `${label} must be less than ${max}`;
      }
    });
    return newErrors;
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;
    let fieldError = "";
    switch (id) {
      case "source":
        if (!value.trim()) fieldError = "Source is required";
        break;
      case "express":
        if (!value.trim()) fieldError = "Please select an Express Type";
        break;
      case "rateCardType":
        if (!value.trim()) fieldError = "Please select a Rate Card Type";
      default:
        break;
    }
    const numericFields = [
      "weight",
      "height",
      "length",
      "width",
      "chargesAmount",
      "fuelCharges",
      "gst",
      "additionalWeightUnit",
      "additionalRate",
    ];
    if (numericFields.includes(id)) {
      if (!value.trim()) {
        fieldError = `${id.charAt(0).toUpperCase() + id.slice(1)} is required`;
      } else if (isNaN(value) || Number(value) < 0) {
        fieldError = `Enter 0 or a positive number for ${id}`;
      } else {
        // Format to 2 decimal places on blur
        setFormData((prev) => ({
          ...prev,
          [id]: formatToTwoDecimals(value),
        }));
      }
    }
    setErrors((prev) => ({ ...prev, [id]: fieldError }));
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      const formattedFormData = {
        // ...formData,
        source: formData.source,
        weight: formData.weight,
        height: formData.height,
        length: formData.length,
        width: formData.width,
        amount: formData.chargesAmount,
        fuelCharges: formData.fuelCharges,
        gst: formData.gst,
        totalCharges: formData?.totalCharges,
        express: formData.express.trim(),
        rateCardId: formData.rateCardType.trim(),
        additionalWeightUnit: formData.additionalWeightUnit,
        additionalRate: formData.additionalRate,
      };
      const apiUrl =
        action === "add"
          ? apiList.CREATE_CHARGES_MASTER
          : apiList.MODIFY_CHARGES_MASTER;
      const payload = {
        username: loggedInUserName,
        ...(isEditMode && {
          id: columnData?.id,
        }),
        ...formattedFormData,
      };
      axiosPostApi(apiUrl, payload)
        .then((response) => {
          if (response?.status === 200 && response?.data?.status === true) {
            CustomAlert({
              title: response?.data?.message,
              icon: "success",
              confirmButtonText: "OK",
            });
            callApi(1);
            handleClose();
            setCurrentPage(1);
          } else {
            CustomAlert({
              title: "Failed",
              text: response.data.message,
              icon: "error",
              confirmButtonText: "Retry",
            });
          }
        })
        .catch(() => {
          CustomAlert({
            title: "Error",
            text: "An error occurred while processing your request.",
            icon: "error",
            confirmButtonText: "Retry",
          });
        });
    }
  };

      useEffect(() => {
        if (!show) return;
        fetchCardData();
      }, [show]);
    
      const fetchCardData = async () => {
        try {
          const response = await axiosGetApi(`${apiList.ALL_RATE_CARD_DATA}?username=${loggedInUserName}`);
    
          if ( response?.data?.status === true &&
              response?.data?.respCode === "00"
            ) {
            setCardOptions(response.data.data);
          }
        } catch (err) {
          console.error("Item fetch error", err);
        }
      };

  const deleteRequest = async () => {
    try {
      const response = await axiosDeleteApi(`${apiList?.DELETE_CHARGES_MASTER}?id=${columnData?.id}&username=${loggedInUserName}`)
      if(response?.data?.status === true &&
          response?.data?.respCode === "00")
        {
          CustomAlert({
          title: response?.data?.message,
          icon: "success",
          confirmButtonText: "OK",
          });
          callApi(1);
          handleClose();
          setShowDeleteModal(false);
          setCurrentPage(1);
        }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while processing your request.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  }

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1031,
      }}
    >
      <div className="bg-white w-[720px] rounded-[20px] p-6 shadow-lg relative border border-gray-200">
        {/* Header */}
        {/* Decorative Circles (background layer) */}
        <img
          src={LeftCircle}
          alt="leftImg"
          className="absolute left-0 bottom-0 w-[110px] h-[105px] z-0"
        />
        <img
          src={RightCircle}
          alt="rightImg"
          className="absolute right-0 top-0 w-[110px] h-[105px] z-0"
        />
        {/* Header Content (foreground layer) */}
        <div
          className="flex justify-between items-center mb-3 relative z-10 pb-3"
          style={{
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <div className="flex items-center gap-2">
            <img src={AddVectorIcon} alt="Add" className="w-7 h-7" />
            <h2 className="text-lg font-semibold text-[#FF6600]">
              {action === "add"
                ? "Add Charges Master"
                : isEditMode
                ? "Modify Charges Master"
                : "View Charges Master"}
            </h2>
          </div>
          {/* Close Icon */}
          <img
            src={CrossIcon}
            alt="Close"
            onClick={handleClose}
            className="w-5 h-5 cursor-pointer hover:rotate-90 transition-transform z-20"
          />
        </div>
        {/* Form Fields */}
        <div className="grid grid-cols-3 gap-4">
          {/* Card Type */}
          <div>
            <Dropdown
              label="Rate Card Type"
              value={formData.rateCardType}
              onChange={(item) => {
                setFormData((prev) => ({
                  ...prev,
                  rateCardType: item.rateCardId,
                }));
            
                setErrors((prev) => ({
                  ...prev,
                  rateCardType: "",
                }));
              }}
              labelKey="rateCardName"
              valueKey="rateCardId"
              options={cardOptions}
              isRequired
              error={errors.rateCardType}
              disabled={isReadOnly}
              selectClassName="w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none"
            />
          </div>
          {/* Source */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Source <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-2 top-2">
                <MapPin size={16} className="text-[#FF6600]" />
              </div>
              <select
                id="source"
                value={formData.source}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isReadOnly}
                className="w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none"
              >
                <option value="">Select Source</option>
                <option value="WITHIN_CITY">Within City</option>
                <option value="WITHIN_ZONE">Within Zone</option>
                <option value="WITHIN_METRO">Within Metro</option>
                <option value="REST_OF_INDIA">Rest of India</option>
                <option value="SPECIAL_INDIA">Special India</option>
                <option value="RECEIVER_METRO">Receiver Metro</option>
              </select>
            </div>
            {errors.source && !isReadOnly && (
              <p className="text-red-500 text-[12px] mt-1">{errors.source}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Express Type <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-2 top-2">
                <Truck size={16} className="text-[#FF6600]" />
              </div>
              <select
                id="express"
                value={formData.express}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isReadOnly}
                className="w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none"
              >
                <option value="">Select Type</option>
                <option value="AIR">Air</option>
                <option value="SURFACE">Surface</option>
              </select>
            </div>
            {errors.express && !isReadOnly && (
              <p className="text-red-500 text-[12px] mt-1">{errors.express}</p>
            )}
          </div>
          <div>
            {/* Label */}
            <label className="text-xs font-medium text-gray-700 mb-1">
              Weight <span className="text-red-500">*</span>
            </label>
            {/* Input + Icon + Unit */}
            <div className="flex items-center">
              {/* Input with icon */}
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2">
                  <Scale size={16} className="text-[#FF6600]" />
                </span>
                <input
                  type="text"
                  id="weight"
                  placeholder="Enter Weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  readOnly={isReadOnly}
                  onWheel={(e) => e.target.blur()}
                  maxLength={7}
                  style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                  className="text-right placeholder:text-left w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white"
                />
              </div>
              {/* Blue KG box */}
              <div className="bg-[#2e237f] text-white text-[13px] font-medium h-[36px] px-3 flex items-center justify-center rounded-r-md">
                kg
              </div>
            </div>
            {/* Error message */}
            {errors.weight && !isReadOnly && (
              <p className="text-red-500 text-[12px] mt-1">{errors.weight}</p>
            )}
          </div>
          {/* Height */}
          <div>
            {/* Label */}
            <label className="text-xs font-medium text-gray-700 mb-1">
              Height <span className="text-red-500">*</span>
            </label>
            {/* Input + Icon + Unit */}
            <div className="flex items-center">
              {/* Input with icon */}
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2">
                  <Ruler size={16} className="text-[#FF6600]" />
                </span>
                <input
                  type="text"
                  id="height"
                  placeholder="Enter Height"
                  value={formData.height}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  readOnly={isReadOnly}
                  onWheel={(e) => e.target.blur()}
                  maxLength={7}
                  style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                  className="text-right placeholder:text-left w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white"
                />
              </div>
              {/* Blue CM box */}
              <div className="bg-[#2e237f] text-white text-[13px] font-medium h-[36px] px-3 flex items-center justify-center rounded-r-md">
                cm
              </div>
            </div>
            {/* Error message */}
            {errors.height && !isReadOnly && (
              <p className="text-red-500 text-[12px] mt-1">{errors.height}</p>
            )}
          </div>
          {/* Length */}
          <div>
            {/* Label */}
            <label className="text-xs font-medium text-gray-700 mb-1">
              Length <span className="text-red-500">*</span>
            </label>
            {/* Input + Icon + Unit */}
            <div className="flex items-center">
              {/* Input with icon */}
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2">
                  <Ruler size={16} className="text-[#FF6600]" />
                </span>
                <input
                  type="text"
                  id="length"
                  placeholder="Enter Length"
                  value={formData.length}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  readOnly={isReadOnly}
                  onWheel={(e) => e.target.blur()}
                  maxLength={7}
                  style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                  className="text-right placeholder:text-left w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white"
                />
              </div>
              {/* Blue CM box */}
              <div className="bg-[#2e237f] text-white text-[13px] font-medium h-[36px] px-3 flex items-center justify-center rounded-r-md">
                cm
              </div>
            </div>
            {/* Enhanced Error Message */}
            {errors.length && !isReadOnly && (
              <p className="text-red-500 text-[12px] font-medium mt-1 leading-tight">
                {errors.length}
              </p>
            )}
          </div>
          {/* Width */}
          <div>
            {/* Label */}
            <label className="text-xs font-medium text-gray-700 mb-1">
              Width <span className="text-red-500">*</span>
            </label>
            {/* Input + Icon + Unit */}
            <div className="flex items-center">
              {/* Input with icon */}
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2">
                  <Package size={16} className="text-[#FF6600]" />
                </span>
                <input
                  type="text"
                  id="width"
                  placeholder="Enter Width"
                  value={formData.width}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  readOnly={isReadOnly}
                  onWheel={(e) => e.target.blur()}
                  maxLength={7}
                  style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                  className="text-right placeholder:text-left w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white"
                />
              </div>
              {/* Blue CM box */}
              <div className="bg-[#2e237f] text-white text-[13px] font-medium h-[36px] px-3 flex items-center justify-center rounded-r-md">
                cm
              </div>
            </div>
            {/* Enhanced Error Message */}
            {errors.width && !isReadOnly && (
              <p className="text-red-500 text-[12px] font-medium mt-1 leading-tight">
                {errors.width}
              </p>
            )}
          </div>
          {/* Amount */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Charges Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-2 top-2">
                <IndianRupee size={16} className="text-[#FF6600]" />
              </div>
              <input
                type="text"
                id="chargesAmount"
                placeholder="Enter Charges Amount"
                value={formData.chargesAmount}
                onChange={handleInputChange}
                onBlur={handleBlur}
                readOnly={isReadOnly}
                onWheel={(e) => e.target.blur()}
                maxLength={15}
                style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                className="w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-right text-[15px] appearance-none focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white"
              />
            </div>
            {errors.chargesAmount && !isReadOnly && (
              <p className="text-red-500 text-[12px] mt-1">
                {errors.chargesAmount}
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Fuel Charges <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-2 top-2">
                <IndianRupee size={16} className="text-[#FF6600]" />
              </div>
              <input
                type="text"
                id="fuelCharges"
                placeholder="Enter Fuel Charges"
                value={formData.fuelCharges}
                onChange={handleInputChange}
                onBlur={handleBlur}
                readOnly={isReadOnly}
                onWheel={(e) => e.target.blur()}
                maxLength={15}
                style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                className="w-full h-[36px] placeholder:text-left rounded-md border border-gray-300 pl-8 pr-2 text-right text-[15px] appearance-none focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white"
              />
            </div>
            {errors.fuelCharges && !isReadOnly && (
              <p className="text-red-500 text-[12px] mt-1">
                {errors.fuelCharges}
              </p>
            )}
          </div>
          {/* GST */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              GST (%) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-2 top-2">
                <Percent size={16} className="text-[#FF6600]" />
              </div>
              <input
                type="text"
                id="gst"
                placeholder="Enter GST"
                value={formData.gst}
                onChange={handleInputChange}
                onBlur={handleBlur}
                readOnly={isReadOnly}
                onWheel={(e) => e.target.blur()}
                maxLength={7}
                style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                className="w-full h-[36px] placeholder:text-left rounded-md border border-gray-300 pl-8 pr-2 text-right text-[15px] appearance-none focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white"
              />
            </div>
            {errors.gst && !isReadOnly && (
              <p className="text-red-500 text-[12px] mt-1">{errors.gst}</p>
            )}
          </div>
          {/* Total Charges */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Total Charges <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-2 top-2">
                <IndianRupee size={16} className="text-[#FF6600]" />
              </div>
              <input
                type="text"
                id="totalCharges"
                placeholder="0.00"
                value={formData.totalCharges}
                disabled
                className="w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-right text-[15px] appearance-none bg-gray-100 cursor-not-allowed"
              />
            </div>
          </div>

          {/* Additional Weight */}
          <div>
            {/* Label */}
            <label className="text-xs font-medium text-gray-700 mb-1">
              Additional Weight <span className="text-red-500">*</span>
            </label>
            {/* Input + Icon + Unit */}
            <div className="flex items-center">
              {/* Input with icon */}
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2">
                  <Scale size={16} className="text-[#FF6600]" />
                </span>
                <input
                  type="text"
                  id="additionalWeightUnit"
                  placeholder="Enter Additional Weight"
                  value={formData.additionalWeightUnit}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  readOnly={isReadOnly}
                  onWheel={(e) => e.target.blur()}
                  maxLength={7}
                  style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                  className="text-right placeholder:text-left w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white"
                />
              </div>
              {/* Blue KG box */}
              <div className="bg-[#2e237f] text-white text-[13px] font-medium h-[36px] px-3 flex items-center justify-center rounded-r-md">
                kg
              </div>
            </div>
            {/* Error message */}
            {errors.additionalWeightUnit && !isReadOnly && (
              <p className="text-red-500 text-[12px] mt-1">{errors.additionalWeightUnit}</p>
            )}
          </div>

          {/*Additional Amount */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Additional Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-2 top-2">
                <IndianRupee size={16} className="text-[#FF6600]" />
              </div>
              <input
                type="text"
                id="additionalRate"
                placeholder="Enter Amount"
                value={formData.additionalRate}
                onChange={handleInputChange}
                onBlur={handleBlur}
                readOnly={isReadOnly}
                onWheel={(e) => e.target.blur()}
                maxLength={15}
                style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                className="w-full h-[36px] placeholder:text-left rounded-md border border-gray-300 pl-8 pr-2 text-right text-[15px] appearance-none focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white"
              />
            </div>
            {errors.additionalRate && !isReadOnly && (
              <p className="text-red-500 text-[12px] mt-1">
                {errors.additionalRate}
              </p>
            )}
          </div>

        </div>
        {/* Footer */}
        <div className="flex justify-center gap-4 mt-6">
          <button
            onClick={handleClose}
            className="btn btn-back !rounded-[8px] w-[133px] h[33px]"
          >
            Cancel
          </button>
          {!isReadOnly && (
            <button
              onClick={handleSubmit}
              className="btn btn-submit !rounded-[8px] w-[133px] h[33px]"
            >
              {isEditMode ? "Update" : "Submit"}
            </button>
          )}

          {isDeleteMode && (
            <button
              type="button"
              className="btn btn-danger !rounded-[8px]"
              onClick={(e) => setShowDeleteModal(true)}
            >
              Delete
            </button>
          )}
        </div>
      </div>
      {showDeleteModal && (
      <GlobalDeleteConfirmationModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        onConfirm={deleteRequest}
      />
      )}
    </div>
  );
};

export default AddChargeMasterModal;
